'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';

interface MarketChartProps {
  symbol: string;
  interval?: string;
  theme?: 'dark' | 'light';
  height?: number;
  width?: number | string;
  studies?: string[];
}

declare global {
  interface Window {
    TradingView: any;
  }
}

export default function MarketChart({ 
  symbol, 
  interval = '1D', 
  theme = 'dark',
  height = 500,
  width = '100%',
  studies = []
}: MarketChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState(interval);

  const getTradingViewSymbol = (symbol: string): string => {
    const symbolUpper = symbol.toUpperCase();
    if (symbolUpper.includes('USDT') || symbolUpper.includes('BTC') || symbolUpper.includes('ETH')) {
      return `BINANCE:${symbolUpper}`;
    }
    if (symbolUpper.includes('_') && (symbolUpper.includes('EUR') || symbolUpper.includes('USD') || symbolUpper.includes('GBP') || symbolUpper.includes('JPY'))) {
      return `OANDA:${symbolUpper}`;
    }
    if (['GC', 'SI', 'CL'].includes(symbolUpper)) {
      return `CME:${symbolUpper}1!`;
    }
    return symbolUpper;
  };

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const activeTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = () => {
      try {
        if (typeof window !== 'undefined' && (window as any).TradingView) {
          new (window as any).TradingView.widget({
            autosize: true,
            symbol: getTradingViewSymbol(symbol),
            interval: selectedInterval,
            timezone: 'Etc/UTC',
            theme: activeTheme,
            style: '1',
            locale: 'en',
            toolbar_bg: activeTheme === 'dark' ? '#0F0F0F' : '#FAFAFA',
            enable_publishing: false,
            allow_symbol_change: true,
            container_id: chartContainerRef.current?.id,
            width: typeof width === 'number' ? width : '100%',
            height: height,
            studies: studies,
            show_popup_button: true,
            popup_width: '1000',
            popup_height: '650',
          });
          setIsLoading(false);
        }
      } catch (error) {
        setError('Failed to initialize TradingView chart');
        setIsLoading(false);
      }
    };
    script.onerror = () => {
      setError('Failed to load TradingView chart');
      setIsLoading(false);
    };
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [symbol, selectedInterval, theme, width, height, studies]);

  return (
    <GlassCard variant="elevated" className="p-0 overflow-hidden relative group">
      <div className="absolute inset-0 bg-brand-teal/5 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none" />
      
      {/* Sovereign Header Overlay */}
      <div className="absolute top-6 left-6 z-20 flex flex-col gap-1 pointer-events-none">
        <motion.div 
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="flex items-center gap-3 bg-bg-secondary/80 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/5 shadow-xl"
        >
          <div className="w-2 h-2 rounded-full bg-brand-teal shadow-glow animate-pulse" />
          <h3 className="text-xl font-black text-text-primary tracking-tighter uppercase">
            {symbol} <span className="text-text-tertiary">/ Neural Link</span>
          </h3>
        </motion.div>
        <div className="flex gap-2 mt-2">
           <div className="px-3 py-1 bg-white/5 backdrop-blur-md rounded-lg text-[9px] font-black uppercase tracking-widest text-text-secondary border border-white/5">Real-Time</div>
           <div className="px-3 py-1 bg-white/5 backdrop-blur-md rounded-lg text-[9px] font-black uppercase tracking-widest text-brand-teal-light border border-brand-teal/20">Alpha V7</div>
        </div>
      </div>

      {/* Institutional Interval Bar */}
      <div className="absolute top-6 right-6 z-20 flex gap-2">
        {['1', '5', '15', '60', '1D', '1W'].map((int) => (
          <GlassButton
            key={int}
            variant="ghost"
            size="sm"
            onClick={() => setSelectedInterval(int)}
            className={`text-[9px] px-3 h-8 min-w-[3rem] tracking-widest font-black uppercase transition-all ${
              selectedInterval === int ? 'bg-brand-teal/20 text-brand-teal-light border-brand-teal/30' : 'text-text-tertiary border-white/5'
            }`}
          >
            {int === '60' ? '1H' : int}
          </GlassButton>
        ))}
      </div>

      {/* Floating Telemetry Pod */}
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="absolute bottom-6 right-6 z-20 bg-bg-secondary/80 backdrop-blur-xl p-4 rounded-[2rem] border border-white/10 shadow-2xl space-y-4 min-w-[12rem] pointer-events-none"
      >
        <div className="flex justify-between items-center gap-6">
           <span className="text-[9px] font-black uppercase tracking-widest text-text-tertiary">Volatility Index</span>
           <span className="text-xs font-mono font-bold text-brand-teal-light">Low Risk</span>
        </div>
        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
           <div className="w-[34%] h-full bg-brand-teal shadow-glow" />
        </div>
        <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest">
           <span className="text-text-secondary">Neural Confidence</span>
           <span className="text-text-primary">92.4%</span>
        </div>
      </motion.div>

      {isLoading && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-bg-primary gap-6">
           <div className="relative">
             <div className="w-20 h-20 rounded-full border-2 border-brand-teal/10 animate-pulse" />
             <div className="absolute inset-0 border-t-2 border-brand-teal rounded-full animate-spin shadow-glow" />
           </div>
           <p className="text-[11px] font-black uppercase tracking-[0.3em] text-brand-teal shadow-glow">Igniting Neural Viz</p>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-bg-primary p-8 text-center gap-4">
          <p className="font-black text-brand-plum-light uppercase tracking-widest">Neural Link Severed</p>
          <p className="text-[10px] text-text-secondary uppercase tracking-widest max-w-xs mx-auto leading-relaxed">{error}</p>
        </div>
      )}
      
      <div 
        id={`tradingview-chart-${symbol}`}
        ref={chartContainerRef}
        className="w-full transition-opacity duration-1000"
        style={{ height: `${height}px`, opacity: isLoading ? 0 : 1 }}
      />
      <div className="absolute bottom-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-teal/20 to-transparent opacity-50" />
    </GlassCard>
  );
}